import { createContext, useContext, useEffect, useMemo, useCallback, ReactNode } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGS, DEFAULT_LANG, isAppLang, type AppLang } from "@/i18n";
import { setAnalyticsLanguage } from "@/lib/analytics";

const STORAGE_KEY = "trbd.lang";

type Direction = "ltr" | "rtl";

interface LanguageContextValue {
    lang: AppLang;
    dir: Direction;
    switchLang: (next: AppLang) => void;
    otherLang: AppLang;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

const dirFor = (lang: AppLang): Direction => (lang === "ar" ? "rtl" : "ltr");

const applyToDocument = (lang: AppLang) => {
    if (typeof document === "undefined") return;
    const dir = dirFor(lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
    document.body.classList.remove("lang-ar", "lang-en");
    document.body.classList.add(`lang-${lang}`);
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
    const params = useParams<{ lang?: string }>();
    const paramLang = params.lang;
    const navigate = useNavigate();
    const location = useLocation();
    const { i18n } = useTranslation();

    const lang: AppLang = isAppLang(paramLang) ? paramLang : DEFAULT_LANG;
    const dir = dirFor(lang);

    useEffect(() => {
        if (i18n.language !== lang) {
            i18n.changeLanguage(lang);
        }
        applyToDocument(lang);
        try {
            localStorage.setItem(STORAGE_KEY, lang);
        } catch {
            // ignore storage errors
        }
        setAnalyticsLanguage(lang);
    }, [lang, i18n]);

    const switchLang = useCallback(
        (next: AppLang) => {
            if (next === lang) return;
            const segments = location.pathname.split("/");
            if (segments.length > 1 && isAppLang(segments[1])) {
                segments[1] = next;
            } else {
                segments.splice(1, 0, next);
            }
            navigate({
                pathname: segments.join("/") || `/${next}`,
                search: location.search,
                hash: location.hash,
            });
        },
        [lang, location, navigate],
    );

    const otherLang: AppLang = lang === "ar" ? "en" : "ar";

    const value = useMemo<LanguageContextValue>(
        () => ({ lang, dir, switchLang, otherLang }),
        [lang, dir, switchLang, otherLang],
    );

    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = (): LanguageContextValue => {
    const ctx = useContext(LanguageContext);
    if (!ctx) {
        throw new Error("useLanguage must be used within LanguageProvider");
    }
    return ctx;
};

export const getInitialLang = (): AppLang => {
    if (typeof window === "undefined") return DEFAULT_LANG;
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (isAppLang(stored)) return stored;
    } catch {
        // ignore
    }
    const nav = window.navigator?.language?.slice(0, 2).toLowerCase();
    if (isAppLang(nav)) return nav;
    return DEFAULT_LANG;
};

export { SUPPORTED_LANGS, type AppLang };
